import { useCallback, useMemo, useRef, useState } from "react";
import {
  DIRS,
  type Dir,
  HEAT_STEPS,
  fmtHHMM,
  fmtPct,
  fmtRiders,
  rampColour,
  stationLabel,
} from "../demand/demandMath";
import { useElementWidth } from "../demand/useElementWidth";
import {
  type Segment,
  defaultWindow,
  fitLabel,
  hourTicks,
  leftBehindByStop,
  nearestSegment,
  stopPositions,
} from "./timeDistanceMath";
import type { SimResult, TripResult } from "./types";
import "./timeDistance.css";

interface Props {
  result: SimResult;
  dirNames: Record<Dir, string>;
  /** "Active timetable", "Draft". */
  title?: string;
  /** Minutes shown on the time axis; the whole hours around the day by default. */
  window?: [number, number];
  /** The replay's clock, drawn as a thin cursor. Updating it does not redraw the trips. */
  cursorMin?: number | null;
  /** Clicking the diagram hands back the time under the pointer. */
  onPickTime?: (min: number) => void;
}

const MARGIN = { right: 12, top: 18, bottom: 24 };
const TIP_W = 260; // matches .dm-tip
const HIT_DIST = 6;
/** Half-width of the ▲ marking riders left behind. */
const MARK = 3.5;

/**
 * Darker is fuller, so fuller stretches are drawn last and stay visible
 * where lines cross. Rank each ramp colour once.
 */
const RAMP_RANK = new Map<string, number>();
for (const dir of DIRS) {
  for (let i = 0; i < HEAT_STEPS; i++) {
    RAMP_RANK.set(rampColour(i / (HEAT_STEPS - 1), dir).fill, i);
  }
}

const clampNum = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/**
 * The time–distance diagram: every trip of the timetable as a line through
 * the day, time along the bottom and the stops down the side, coloured by how
 * full the bus is on each stretch.
 *
 * It is the planner's view of a timetable. The slope is the run time, the
 * gap between lines is the headway, and a dark stretch followed by a ▲ is a
 * full bus leaving people at the stop. Stops are spaced by scheduled run
 * time, so lines are straight and a bend would mean something.
 */
export default function TimeDistanceChart({ result, dirNames, title, window: range, cursorMin, onPickTime }: Props) {
  const [wrapRef, width] = useElementWidth<HTMLDivElement>();
  const svgRef = useRef<SVGSVGElement>(null);
  const [asTable, setAsTable] = useState(false);
  const [hover, setHover] = useState<{ trip: number; link: number; px: number; py: number } | null>(null);

  const stops = result.input.stops;
  const n = stops.length;
  const capacity = result.capacity;

  const [r0, r1] = range ?? defaultWindow(result);
  const height = clampNum(24 + 26 * n + 40, 180, 420);
  const plotH = height - MARGIN.top - MARGIN.bottom;
  // The label column narrows with the chart; names shrink to initials to fit.
  const labelW = width >= 900 ? 140 : width >= 640 ? 110 : 64;
  const maxChars = Math.floor((labelW - 10) / 6.4);

  const geo = useMemo(() => {
    if (width <= 0 || n === 0 || r1 <= r0) return null;
    const plotW = Math.max(0, width - labelW - MARGIN.right);
    const x = (t: number) => labelW + ((t - r0) / (r1 - r0)) * plotW;
    const pos = stopPositions(result);
    const y = (s: number) => MARGIN.top + (pos[s] ?? 0) * plotH;

    const byFill = new Map<string, string[]>();
    const segments: Segment[] = [];
    const marks: string[] = [];
    result.trips.forEach((tr, ti) => {
      const calls = tr.trip.calls;
      if (calls.length < 2) return;
      if (calls[calls.length - 1].timeMin < r0 || calls[0].timeMin > r1) return;
      for (let i = 0; i < calls.length - 1; i++) {
        const a = calls[i];
        const b = calls[i + 1];
        const x1 = x(a.timeMin), y1 = y(a.stop), x2 = x(b.timeMin), y2 = y(b.stop);
        const fill = rampColour((tr.linkLoads[i] ?? 0) / capacity, tr.trip.dir).fill;
        let parts = byFill.get(fill);
        if (!parts) { parts = []; byFill.set(fill, parts); }
        parts.push(`M${x1.toFixed(1)},${y1.toFixed(1)}L${x2.toFixed(1)},${y2.toFixed(1)}`);
        segments.push({ x1, y1, x2, y2, trip: ti, link: i });
      }
      for (const c of tr.calls) {
        if (c.leftBehind < 0.5) continue;
        const cx = x(c.timeMin), cy = y(c.stop);
        marks.push(`M${(cx - MARK).toFixed(1)},${(cy + MARK).toFixed(1)}L${(cx + MARK).toFixed(1)},${(cy + MARK).toFixed(1)}L${cx.toFixed(1)},${(cy - MARK).toFixed(1)}Z`);
      }
    });
    const layers = [...byFill.entries()].sort((a, b) => (RAMP_RANK.get(a[0]) ?? 0) - (RAMP_RANK.get(b[0]) ?? 0));
    return { plotW, x, y, layers, segments, marks: marks.join("") };
  }, [result, r0, r1, width, labelW, plotH, n, capacity]);

  // The trips are the expensive part; they are rebuilt only with the geometry.
  // The cursor and the hover highlight live in their own layers above.
  const tripLayer = useMemo(() => {
    if (!geo) return null;
    return (
      <g className="sim-td-trips">
        {geo.layers.map(([fill, parts]) => (
          <path key={fill} className="sim-td-line" d={parts.join("")} stroke={fill} />
        ))}
        {geo.marks && <path className="sim-td-mark" d={geo.marks} />}
      </g>
    );
  }, [geo]);

  const toPointer = useCallback((e: React.PointerEvent | React.MouseEvent) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return { px: e.clientX - rect.left, py: e.clientY - rect.top };
  }, []);

  const onMove = (e: React.PointerEvent<SVGRectElement>) => {
    if (!geo) return;
    const pt = toPointer(e);
    if (!pt) return;
    const seg = nearestSegment(geo.segments, pt.px, pt.py, HIT_DIST);
    setHover(seg ? { trip: seg.trip, link: seg.link, px: pt.px, py: pt.py } : null);
  };

  const onClick = (e: React.MouseEvent<SVGRectElement>) => {
    if (!geo || !onPickTime) return;
    const pt = toPointer(e);
    if (!pt) return;
    const t = r0 + ((pt.px - labelW) / Math.max(1, geo.plotW)) * (r1 - r0);
    onPickTime(Math.round(clampNum(t, r0, r1)));
  };

  const hovered: TripResult | null = hover ? result.trips[hover.trip] ?? null : null;

  const linkName = (tr: TripResult, link: number) => {
    const a = tr.trip.calls[link];
    const b = tr.trip.calls[link + 1];
    if (!a || !b) return "";
    return `${stationLabel(stops[a.stop]?.name ?? "")} → ${stationLabel(stops[b.stop]?.name ?? "")}`;
  };

  // Beside the pointer, never under it: to its right while there is room.
  const tipLeft = hover ? (hover.px + 14 + TIP_W <= width ? hover.px + 14 : Math.max(0, hover.px - 14 - TIP_W)) : 0;
  const tipTop = hover ? clampNum(hover.py - 24, 0, Math.max(0, height - 120)) : 0;

  const ticks = geo ? hourTicks(r0, r1, geo.plotW / ((r1 - r0) / 60)) : [];
  const full = result.trips.filter((t) => t.leftBehind >= 0.5).length;

  return (
    <div className="dm-chart sim-td-chart">
      <div className="dm-chart-head">
        <div>
          <h4>{title ?? "Trips through the day"}</h4>
          <p className="dm-chart-sub">
            Each line is one trip, time along the bottom and stops down the side. Its colour is how full the bus is
            on that stretch; ▲ is a full bus leaving riders at the stop.
          </p>
        </div>
        <div className="sched-segmented dm-view-toggle" role="group" aria-label="View as">
          <button type="button" className={!asTable ? "active" : ""} onClick={() => setAsTable(false)}>Chart</button>
          <button type="button" className={asTable ? "active" : ""} onClick={() => setAsTable(true)}>Table</button>
        </div>
      </div>

      {asTable && <TripTable result={result} dirNames={dirNames} linkName={linkName} full={full} />}

      {/* Hidden rather than unmounted in table view: the width observer stays
          attached, so the chart comes back at its real size when switched to. */}
      <div className="sim-td" ref={wrapRef} hidden={asTable}>
          {width > 0 && geo && (
            <svg ref={svgRef} width={width} height={height} role="img"
              aria-label={`Time–distance diagram of ${result.trips.length} trips, ${full} of which left riders behind`}>
              {/* grid: a line per stop, a line per hour; both recessive */}
              {stops.map((s) => (
                <g key={s.index}>
                  <line className="dm-gridline" x1={labelW} x2={labelW + geo.plotW} y1={geo.y(s.index)} y2={geo.y(s.index)} />
                  <text className="sim-td-stop-label" x={labelW - 8} y={geo.y(s.index)} dy="0.32em" textAnchor="end">
                    <title>{s.name}</title>
                    {fitLabel(s.name, maxChars)}
                  </text>
                </g>
              ))}
              {ticks.map((t) => (
                <g key={t}>
                  <line className={t === r0 ? "dm-axis" : "dm-gridline"} x1={geo.x(t)} x2={geo.x(t)} y1={MARGIN.top - 4} y2={MARGIN.top + plotH} />
                  <text className="dm-tick" x={geo.x(t)} y={MARGIN.top + plotH + 15}
                    textAnchor={t === r0 ? "start" : t === r1 ? "end" : "middle"}>
                    {fmtHHMM(t)}
                  </text>
                </g>
              ))}

              <g className={hover ? "sim-td-dim" : undefined}>{tripLayer}</g>

              {result.trips.length === 0 && (
                <text className="sim-td-empty" x={labelW + geo.plotW / 2} y={MARGIN.top + plotH / 2} textAnchor="middle">
                  No trips in this timetable
                </text>
              )}

              {hovered && (
                <g className="sim-td-hi" aria-hidden>
                  <path className="sim-td-halo"
                    d={hovered.trip.calls.map((c, i) => `${i === 0 ? "M" : "L"}${geo.x(c.timeMin).toFixed(1)},${geo.y(c.stop).toFixed(1)}`).join("")} />
                  {hovered.linkLoads.map((load, i) => {
                    const a = hovered.trip.calls[i];
                    const b = hovered.trip.calls[i + 1];
                    return (
                      <path key={i} className="sim-td-line" stroke={rampColour(load / capacity, hovered.trip.dir).fill}
                        d={`M${geo.x(a.timeMin).toFixed(1)},${geo.y(a.stop).toFixed(1)}L${geo.x(b.timeMin).toFixed(1)},${geo.y(b.stop).toFixed(1)}`} />
                    );
                  })}
                  {hovered.calls.filter((c) => c.leftBehind >= 0.5).map((c, i) => (
                    <path key={`m${i}`} className="sim-td-mark"
                      d={`M${geo.x(c.timeMin) - MARK - 1},${geo.y(c.stop) + MARK + 1}L${geo.x(c.timeMin) + MARK + 1},${geo.y(c.stop) + MARK + 1}L${geo.x(c.timeMin)},${geo.y(c.stop) - MARK - 1}Z`} />
                  ))}
                </g>
              )}

              {cursorMin != null && cursorMin >= r0 && cursorMin <= r1 && (
                <g className="sim-td-cursor" aria-hidden>
                  <line x1={geo.x(cursorMin)} x2={geo.x(cursorMin)} y1={MARGIN.top - 4} y2={MARGIN.top + plotH} />
                  <text x={geo.x(cursorMin)} y={MARGIN.top - 7} textAnchor="middle">{fmtHHMM(cursorMin)}</text>
                </g>
              )}

              {/* one hit target over the plot; trips are found by distance to the pointer */}
              <rect className={`sim-td-hit${onPickTime ? " sim-td-hit-pick" : ""}`}
                x={labelW} y={MARGIN.top - 4} width={geo.plotW} height={plotH + 4}
                onPointerMove={onMove} onPointerLeave={() => setHover(null)} onClick={onClick} />
            </svg>
          )}

          {hover && hovered && geo && (
            <div className="dm-tip" role="status" style={{ left: tipLeft, top: tipTop }}>
              <div className="dm-tip-title">
                {fmtHHMM(hovered.trip.calls[0].timeMin)} {dirNames[hovered.trip.dir]}
                {hovered.trip.block > 0 ? ` · block ${hovered.trip.block}` : ""}
              </div>
              <div className="dm-tip-row">
                <span className={`dm-linekey dm-linekey-${hovered.trip.dir}`} aria-hidden />
                <div>
                  <div><strong>{fmtRiders(hovered.linkLoads[hover.link] ?? 0)}</strong> aboard here</div>
                  <div className="dm-tip-detail">{linkName(hovered, hover.link)}</div>
                </div>
              </div>
              <div className="dm-tip-row">
                <span className="dm-linekey sim-td-linekey-blank" aria-hidden />
                <div>
                  <div>
                    <strong>{fmtRiders(hovered.maxLoad)}</strong> of {capacity} at the fullest
                    {hovered.maxLoad > 0 ? ` (${fmtPct(hovered.loadFactor)})` : ""}
                  </div>
                  <div className="dm-tip-detail">
                    {hovered.maxLoadLink >= 0 ? linkName(hovered, hovered.maxLoadLink) : "carried nobody"}
                    {" · "}{fmtRiders(hovered.riders)} riders on the trip
                  </div>
                </div>
              </div>
              {hovered.leftBehind >= 0.5 && (
                <div className="dm-tip-foot dm-flag">
                  ▲ {leftBehindByStop(hovered.calls)
                    .map((l) => `${fmtRiders(Math.round(l.riders))} left behind at ${stationLabel(stops[l.stop]?.name ?? "")}`)
                    .join(", ")}
                </div>
              )}
            </div>
          )}

          <div className="dm-legend">
            {DIRS.map((dir) => (
              <span className="dm-legend-item" key={dir}>
                <span className="sim-td-ramp" aria-hidden>
                  {Array.from({ length: HEAT_STEPS }, (_, i) => (
                    <span key={i} style={{ background: rampColour(i / (HEAT_STEPS - 1), dir).fill }} />
                  ))}
                </span>
                {dirNames[dir]} <span className="sim-td-note">empty → full</span>
              </span>
            ))}
            <span className="dm-legend-item">
              <svg width="10" height="8" aria-hidden><path className="sim-td-mark" d="M1,7 L9,7 L5,1 Z" /></svg>
              Full bus left riders at the stop
            </span>
            {onPickTime && <span className="dm-legend-hint">Click to move the replay to that time.</span>}
          </div>
      </div>
    </div>
  );
}

function TripTable({
  result, dirNames, linkName, full,
}: {
  result: SimResult;
  dirNames: Record<Dir, string>;
  linkName: (tr: TripResult, link: number) => string;
  full: number;
}) {
  const fullest = result.trips.reduce<TripResult | null>((best, t) => (!best || t.maxLoad > best.maxLoad ? t : best), null);
  return (
    <div>
      <p className="sim-td-summary">
        {result.trips.length} trips
        {full > 0 ? `, ${full} left riders behind` : ", none left riders behind"}
        {fullest && fullest.maxLoad > 0
          ? ` · fullest ${fmtHHMM(fullest.trip.calls[0].timeMin)} ${dirNames[fullest.trip.dir]}, ${fmtRiders(fullest.maxLoad)} of ${result.capacity} aboard`
          : ""}
      </p>
      <div className="dm-table-scroll sim-td-table-scroll">
        <table className="data-table compact dm-table">
          <thead>
            <tr>
              <th>Departs</th>
              <th>Direction</th>
              <th className="num">Block</th>
              <th className="num">Riders</th>
              <th className="num">Fullest</th>
              <th>Where</th>
              <th className="num">Left behind</th>
            </tr>
          </thead>
          <tbody>
            {result.trips.map((t) => (
              <tr key={t.trip.id}>
                <td>{fmtHHMM(t.trip.calls[0].timeMin)}</td>
                <td><span className={`dm-swatch dm-swatch-${t.trip.dir}`} aria-hidden /> {dirNames[t.trip.dir]}</td>
                <td className="num">{t.trip.block > 0 ? t.trip.block : "–"}</td>
                <td className="num">{fmtRiders(t.riders)}</td>
                <td className="num">
                  {fmtRiders(t.maxLoad)} of {result.capacity}{t.maxLoad > 0 ? ` (${fmtPct(t.loadFactor)})` : ""}
                </td>
                <td>{t.maxLoadLink >= 0 ? linkName(t, t.maxLoadLink) : "–"}</td>
                <td className="num">
                  {t.leftBehind >= 0.5 ? <span className="dm-flag">▲ {fmtRiders(Math.round(t.leftBehind))}</span> : "–"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
